/**
 * DocumentsPanel — Belege section of the farm object view: the
 * provenance anchor (design principle 4). Label chain
 * docTypeLabels[doc_type] → filename → 'Dokument' is empty-string-safe
 * (bug B6: `''` beat `??` fallbacks and rendered bare green brackets).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2 } from 'lucide-react';
import { linkDetails } from '../../../components/twinLinks/linkFormat';
import { linkTypeLabel } from '../../../services/linkTypeLabels';
import type { DocumentLink, EntitySourceDoc } from '../../../services/twinService';
import './DocumentsPanel.css';

const DOC_TYPE_LABELS: Record<string, string> = {
    delivery_note: 'Lieferschein',
    'delivery-note': 'Lieferschein',
    credit_note: 'Gutschrift',
    'credit-note': 'Gutschrift',
    invoice: 'Rechnung',
    contract: 'Vertrag',
    framework_agreement: 'Rahmenvertrag',
    payment: 'Zahlung',
    settlement: 'Abrechnung',
    receipt: 'Beleg',
    'nmin-analysis': 'Bodenprobe',
    'humus-analysis': 'Humusanalyse',
    'soil-analysis-report': 'Bodenanalyse',
    'soil-analysis-explanation': 'Bodenanalyse',
    'soil-testing-other': 'Bodenuntersuchung',
    'wage-statement': 'Lohnabrechnung',
    'employment-contract': 'Arbeitsvertrag',
    'registration-notice': 'Meldebescheinigung',
    'annual-wage-statement': 'Lohnsteuerbescheinigung',
    'training-certificate': 'Schulungsnachweis',
    'health-insurance-doc': 'Krankenkassenmeldung',
    'social-insurance-statement': 'Sozialversicherungsnachweis',
    'sick-leave-certificate': 'Arbeitsunfähigkeitsbescheinigung',
    'contract-amendment': 'Vertragsänderung',
    'termination-notice': 'Kündigung',
    'residence-permit': 'Aufenthaltserlaubnis',
    'mini-job-documentation': 'Minijob-Unterlagen',
    'qualification-certificate': 'Qualifikationsnachweis',
    'safety-training': 'Sicherheitsunterweisung',
    'apprenticeship-docs': 'Ausbildungsunterlagen',
    'pension-insurance-doc': 'Rentenversicherungsnachweis',
    'social-insurance-other': 'Sozialversicherung (Sonstiges)',
    other: 'Sonstiges',
};

export function documentLabel(doc: EntitySourceDoc): string {
    const typeLabel = doc.doc_type ? DOC_TYPE_LABELS[doc.doc_type] : undefined;
    // `||` on purpose: empty strings must fall through (bug B6).
    return typeLabel || doc.filename || (doc.doc_type ? doc.doc_type.replace(/-/g, ' ') : '') || 'Dokument';
}

export interface DocumentsPanelProps {
    docs?: EntitySourceDoc[];
    isLoading?: boolean;
    isError?: boolean;
    onPreview?: (docId: string) => void;
    /**
     * FR-017.7 — this object's live link rows, by document. A tile with none
     * (the facts alone put it there) gets no link control: there is nothing
     * to supersede. Absent entirely = read-only (the as-of view).
     */
    linksByDoc?: Map<string, DocumentLink[]>;
    onUnlink?: (linkId: string) => Promise<unknown>;
}

/**
 * One tile's links, each removable behind an inline confirm — the same
 * affordance as the document's "Verknüpfte Objekte", from the other side.
 * Its own component so each tile keeps its own open/confirm state.
 */
const TileLinks: React.FC<{
    title: string;
    links: DocumentLink[];
    onUnlink: (linkId: string) => Promise<unknown>;
}> = ({ title, links, onUnlink }) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [pending, setPending] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [failed, setFailed] = React.useState(false);

    const confirm = async (linkId: string) => {
        setBusy(true);
        setFailed(false);
        try {
            await onUnlink(linkId);
            setPending(null);
        } catch {
            setFailed(true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="documents-panel__links">
            <button
                type="button"
                className="documents-panel__links-toggle"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                {t('twinLinks.objectSide.linksToggle', { count: links.length })}
            </button>
            {open && (
                <ul className="documents-panel__links-list" aria-label={t('twinLinks.objectSide.linksOf', { title })}>
                    {links.map((link) => (
                        <li key={link.link_id} className="documents-panel__link">
                            <span>{linkTypeLabel(link.link_type)}</span>
                            {linkDetails(link).map((d) => (
                                <span key={d} className="documents-panel__link-detail">
                                    {d}
                                </span>
                            ))}
                            {pending === link.link_id ? (
                                <span className="documents-panel__confirm">
                                    <span>{t('twinLinks.confirmRemove')}</span>
                                    <button type="button" disabled={busy} onClick={() => void confirm(link.link_id)}>
                                        {t('twinLinks.confirm')}
                                    </button>
                                    <button type="button" onClick={() => setPending(null)}>
                                        {t('twinLinks.cancel')}
                                    </button>
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    aria-label={t('twinLinks.objectSide.removeNamed', {
                                        name: [linkTypeLabel(link.link_type), ...linkDetails(link)].join(' '),
                                    })}
                                    onClick={() => setPending(link.link_id)}
                                >
                                    {t('twinLinks.confirm')}
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {failed && (
                <p role="alert" className="documents-panel__error">
                    {t('twinLinks.removeError')}
                </p>
            )}
        </div>
    );
};

export const DocumentsPanel: React.FC<DocumentsPanelProps> = ({
    docs = [],
    isLoading,
    isError,
    onPreview,
    linksByDoc,
    onUnlink,
}) => {
    if (isLoading) {
        return (
            <div className="documents-panel__loading">
                <Loader2 size={14} className="documents-panel__spinner" />
                Belege werden geladen…
            </div>
        );
    }
    if (isError) {
        return <p className="documents-panel__none">Belege konnten nicht geladen werden</p>;
    }
    if (docs.length === 0) {
        return <p className="documents-panel__none">Keine verknüpften Dokumente</p>;
    }

    const sorted = [...docs].sort((a, b) => {
        if (a.document_date === b.document_date) return 0;
        if (!a.document_date) return 1;
        if (!b.document_date) return -1;
        return b.document_date.localeCompare(a.document_date);
    });

    return (
        <div className="documents-panel">
            {sorted.map((doc) => {
                const dateStr = doc.document_date
                    ? new Date(doc.document_date).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                      })
                    : null;
                const links = linksByDoc?.get(doc.document_id) ?? [];
                return (
                    // The link control is a SIBLING of the tile, not inside it: a
                    // button in a button is invalid, and opening the list must not
                    // also open the preview.
                    <div key={doc.document_id} className="documents-panel__item">
                        <button
                            type="button"
                            className="documents-panel__tile"
                            onClick={onPreview ? () => onPreview(doc.document_id) : undefined}
                        >
                            <FileText size={14} className="documents-panel__icon" />
                            <span className="documents-panel__body">
                                <span className="documents-panel__title">{documentLabel(doc)}</span>
                                {doc.filename && doc.filename !== documentLabel(doc) && (
                                    <span className="documents-panel__filename">{doc.filename}</span>
                                )}
                            </span>
                            {/* FR-017: what this document asserts about the object
                                (`generic` is filtered out by the backend). `?? []`
                                because a backend that predates the field sends none. */}
                            {(doc.link_types ?? []).map((lt) => (
                                <span
                                    key={lt}
                                    className="documents-panel__link-type"
                                    data-testid="documents-panel-link-type"
                                >
                                    {linkTypeLabel(lt)}
                                </span>
                            ))}
                            {dateStr && <span className="documents-panel__date">{dateStr}</span>}
                        </button>
                        {onUnlink && links.length > 0 && (
                            <TileLinks title={documentLabel(doc)} links={links} onUnlink={onUnlink} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default DocumentsPanel;
